import { useEffect, useState } from 'react'
import { Button, Card, Col, Row, Table, Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { FaTrash, FaEdit, FaCheck, FaTimes  } from 'react-icons/fa'
import {
  getPoshCommittees,
  deletePoshCommittee,
  deletePoshCommitteeMember,
  updatePoshCommittee,
  addPoshCommitteeMember,
  getPoshCommitteeMembers,
} from '../../../api/IncidentAPI'
import { format } from 'date-fns'

interface PoshCommitteeListProps {
  onTabChange: (tab: string) => void
}

const PoshCommitteeList: React.FC<PoshCommitteeListProps> = ({
  onTabChange,
}) => {
  const [committees, setCommittees] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [editingCommitteeId, setEditingCommitteeId] = useState<string | null>(null)
  const [editingCommitteeName, setEditingCommitteeName] = useState('')
  const [showAddMember, setShowAddMember] = useState(false)
  const [selectedCommitteeId, setSelectedCommitteeId] =
    useState<string | null>(null)
  const [poshMembers, setPoshMembers] = useState<any[]>([])
  const [selectedMemberId, setSelectedMemberId] = useState('')
  const [addingMember, setAddingMember] = useState(false)
  const [pendingRemoval, setPendingRemoval] = useState<{
    type: 'committee' | 'member'
    committeeId: string
    userId?: string
    name: string
  } | null>(null)

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  })

  useEffect(() => {
    fetchCommittees()
  }, [pagination.page])

  const fetchCommittees = async () => {
    setLoading(true)

    try {
      const response = await getPoshCommittees({
        page: pagination.page,
        limit: pagination.limit,
      })

      if (response.statusCode === 200) {
        setCommittees(response.data.committees || [])
        setPagination(response.data.pagination || pagination)
      } else {
        toast.error(
          response.message || 'Failed to fetch POSH committees'
        )
      }
    } catch (error) {
      console.error('Error fetching POSH committees:', error)
      toast.error('Failed to fetch POSH committees')
    } finally {
      setLoading(false)
    }
  }
  const handleEditCommittee = (
  id: string,
  name: string
  ) => {
    setEditingCommitteeId(id)
    setEditingCommitteeName(name)
  }

  const handleCancelEdit = () => {
    setEditingCommitteeId(null)
    setEditingCommitteeName('')
  }
  const handleOpenAddMember = async (committeeId: string) => {
  setSelectedCommitteeId(committeeId)
  setSelectedMemberId('')
  setShowAddMember(true)
    try {
      const response = await getPoshCommitteeMembers()
      if (response.statusCode === 200) {
        setPoshMembers(response.data || [])
      } else {
        toast.error(
          response.message || 'Failed to fetch POSH members'
        )
      }
    } catch (error) {
      console.error('Error fetching POSH members:', error)
      toast.error('Failed to fetch POSH members')
    }
  }
  const handleAddMember = async () => {
    if (!selectedCommitteeId) {
      return
    }
    if (!selectedMemberId) {
      toast.error('Please select an employee')
      return
    }
    setAddingMember(true)
    try {
      const response = await addPoshCommitteeMember(
        selectedCommitteeId,
        selectedMemberId
      )
      if (response.statusCode === 201) {
        toast.success(
          'Employee added to POSH Committee successfully'
        )
        setShowAddMember(false)
        setSelectedMemberId('')
        await fetchCommittees()
      } else {
        toast.error(
          response.message ||
            'Failed to add employee to POSH Committee'
        )
      }
    } catch (error) {
      console.error(
        'Error adding employee to POSH Committee:',
        error
      )
      toast.error(
        'Failed to add employee to POSH Committee'
      )
    } finally {
      setAddingMember(false)
    }
  }
  const handleSaveCommitteeName = async (id: string) => {
    const name = editingCommitteeName.trim()
    if (!name) {
      toast.error('Committee name is required')
      return
    }
    const normalizedName = name.replace(/\s+/g, ' ').toLocaleLowerCase()
    if (committees.some(item => item.id !== id && item.name.trim().replace(/\s+/g, ' ').toLocaleLowerCase() === normalizedName)) {
      toast.error('A committee with this name already exists')
      return
    }
    try {
      const response = await updatePoshCommittee(id, {
        name: name.replace(/\s+/g, ' '),
      })
      if (response.statusCode === 200) {
        toast.success('POSH Committee name updated successfully')
        setEditingCommitteeId(null)
        setEditingCommitteeName('')
        fetchCommittees()
      } else {
        toast.error(
          response.message ||
          'Failed to update POSH Committee name'
        )
      }
    } catch (error) {
      console.error(
        'Error updating POSH Committee name:',
        error
      )
      toast.error(
        'Failed to update POSH Committee name'
      )
    }
  }
  const handleDelete = (id: string, name: string) => {
    setPendingRemoval({ type: 'committee', committeeId: id, name })
  }

  const handleDeleteMember = (committeeId: string, userId: string, memberName: string) => {
    setPendingRemoval({ type: 'member', committeeId, userId, name: memberName })
  }

  const confirmRemoval = async () => {
    if (!pendingRemoval) return
    try {
      const response = pendingRemoval.type === 'committee'
        ? await deletePoshCommittee(pendingRemoval.committeeId)
        : await deletePoshCommitteeMember(pendingRemoval.committeeId, pendingRemoval.userId!)
      if (response.statusCode === 202) {
        if (pendingRemoval.type === 'committee') {
          setCommittees(current => current.filter(item => item.id !== pendingRemoval.committeeId))
          setPagination(current => ({ ...current, total: Math.max(0, current.total - 1) }))
          setSelectedCommitteeId(current => current === pendingRemoval.committeeId ? null : current)
          toast.success('POSH Committee removed')
        } else {
          toast.success('Committee member removed')
          await fetchCommittees()
        }
      } else {
        toast.error(response.message || 'Failed to remove item')
      }
    } catch (error) {
      console.error('Error removing committee item:', error)
      toast.error('Failed to remove item')
    } finally {
      setPendingRemoval(null)
    }
  }
  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({
      ...prev,
      page: newPage,
    }))
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">POSH Committees</h4>

        <Button
          variant="success"
          onClick={() => onTabChange('posh-committee')}
        >
          Create New Committee
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div
            className="spinner-border text-primary"
            role="status"
          >
            <span className="visually-hidden">
              Loading...
            </span>
          </div>
        </div>
      ) : committees.length === 0 ? (
        <Card className="text-center py-5">
          <Card.Body>
            <h5 className="text-muted">
              No POSH Committees Found
            </h5>

            <p className="text-muted">
              Create your first POSH Committee to get started.
            </p>

            <Button
              variant="success"
              onClick={() =>
                onTabChange('posh-committee')
              }
            >
              Create Committee
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Row>
          {committees.map((committee) => (
            <Col
              key={committee.id}
              md={12}
              className="mb-4"
            >
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <div>
                    {editingCommitteeId === committee.id ? (
                      <div className="d-flex align-items-center gap-2">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={editingCommitteeName}
                          onChange={(e) =>
                            setEditingCommitteeName(e.target.value)
                          }
                          autoFocus
                        />
                        <Button
                          variant="success"
                          size="sm"
                          title="Save"
                          onClick={() =>
                            handleSaveCommitteeName(committee.id)
                          }
                        >
                          <FaCheck />
                        </Button>

                        <Button
                          variant="secondary"
                          size="sm"
                          title="Cancel"
                          onClick={handleCancelEdit}
                        >
                          <FaTimes />
                        </Button>
                      </div>
                    ) : (
                      <div className="d-flex align-items-center gap-2">
                        <h5 className="mb-0">{committee.name}</h5>

                        <Button
                          variant="outline-primary"
                          size="sm"
                          title="Edit Committee Name"
                          onClick={() =>
                            handleEditCommittee(
                              committee.id,
                              committee.name
                            )
                          }
                        >
                          <FaEdit />
                        </Button>
                      </div>
                    )}

                    <small className="text-muted">
                      Created on{' '}
                      {format(
                        new Date(committee.createdAt),
                        'MMM dd, yyyy'
                      )}
                    </small>
                  </div>

                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() =>
                      handleDelete(
                        committee.id,
                        committee.name
                      )
                    }
                  >
                    Delete
                  </Button>
                </Card.Header>

                <Card.Body>
                  {committee.description && (
                    <p className="text-muted mb-3">
                      {committee.description}
                    </p>
                  )}

                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="fw-bold mb-0">
                      Committee Members ({committee.members?.length || 0})
                    </h6>

                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() =>
                        handleOpenAddMember(committee.id)
                      }
                    >
                      + Add Member
                    </Button>
                  </div>

                  {committee.members &&
                  committee.members.length > 0 ? (
                    <Table
                      striped
                      bordered
                      hover
                      size="sm"
                    >
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Employee Code</th>
                          <th>Email</th>
                          <th>Department</th>
                          <th>Designation</th>
                          <th>Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {committee.members.map(
                          (member: any) => {
                            const memberName = `${member.user.firstName} ${member.user.lastName}`

                            return (
                              <tr key={member.id}>
                                <td>
                                  <strong>
                                    {memberName}
                                  </strong>
                                </td>

                                <td>
                                  {member.user.employeeCode ||
                                    'N/A'}
                                </td>

                                <td>
                                  {member.user.email}
                                </td>
                                <td>
                                  {member.user.department?.name || 'N/A'}
                                </td>

                                <td>
                                  {member.user.designation
                                    ?.name || 'N/A'}
                                </td>

                                <td>
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    title="Remove employee"
                                    onClick={() =>
                                      handleDeleteMember(
                                        committee.id,
                                        member.user.id,
                                        memberName
                                      )
                                    }
                                  >
                                    <FaTrash />
                                  </Button>
                                </td>
                              </tr>
                            )
                          }
                        )}
                      </tbody>
                    </Table>
                  ) : (
                    <p className="text-muted">
                      No members assigned to this committee.
                    </p>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {pagination.totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-4">
          <Button
            variant="secondary"
            disabled={pagination.page === 1}
            onClick={() =>
              handlePageChange(
                pagination.page - 1
              )
            }
          >
            Previous
          </Button>

          <span className="text-muted">
            Page {pagination.page} of{' '}
            {pagination.totalPages}
          </span>

          <Button
            variant="secondary"
            disabled={
              pagination.page ===
              pagination.totalPages
            }
            onClick={() =>
              handlePageChange(
                pagination.page + 1
              )
            }
          >
            Next
          </Button>
        </div>
      )}
      {showAddMember && (
        <div
          className="modal d-block"
          tabIndex={-1}
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Add Committee Member
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAddMember(false)}
                />
              </div>
              <div className="modal-body">
                <label className="form-label">
                  Select Employee
                </label>
                <select
                  className="form-select"
                  value={selectedMemberId}
                  onChange={(e) =>
                    setSelectedMemberId(e.target.value)
                  }
                >
                  <option value="">
                    Select Employee
                  </option>
                  {poshMembers
                    .filter((member) => {
                      const committee = committees.find(
                        (c) =>
                          c.id === selectedCommitteeId
                      )

                      return !committee?.members?.some(
                        (existingMember: any) =>
                          existingMember.user.id ===
                          member.id
                      )
                    })
                    .map((member) => (
                      <option
                        key={member.id}
                        value={member.id}
                      >
                        {member.firstName} {member.lastName}
                        {' - '}
                        {member.employeeCode}
                      </option>
                    ))}
                </select>
              </div>
              <div className="modal-footer">
                <Button
                  variant="secondary"
                  onClick={() =>
                    setShowAddMember(false)
                  }
                  disabled={addingMember}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleAddMember}
                  disabled={addingMember}
                >
                  {addingMember
                    ? 'Adding...'
                    : 'Add Member'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      <Modal show={!!pendingRemoval} onHide={() => setPendingRemoval(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{pendingRemoval?.type === 'committee' ? 'Remove POSH Committee' : 'Remove Committee Member'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingRemoval?.type === 'committee'
            ? `Remove "${pendingRemoval.name}"? Its member associations will also be removed.`
            : `Remove "${pendingRemoval?.name}" from this POSH Committee?`}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPendingRemoval(null)}>Cancel</Button>
          <Button variant="danger" onClick={confirmRemoval}>Remove</Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default PoshCommitteeList
